// Descriptive statistics helpers: central tendency (mean/median/mode),
// dispersion (variance/standard deviation/range/IQR/CV), position
// (z-score/percentile/quartiles), and distribution summaries (five-number
// summary, outliers, frequency table). All take a raw comma/newline/space
// separated list of numbers as input.
import { t } from './i18n'

function parseData(raw: unknown): number[] {
    return String(raw || '')
        .split(/[,\s\n]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map(Number)
        .filter((n) => !isNaN(n))
}

function mean(arr: number[]): number {
    return arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0
}

function medianOf(arr: number[]): number {
    if (arr.length === 0) return 0
    const sorted = [...arr].sort((a, b) => a - b)
    const n = sorted.length
    const mid = Math.floor(n / 2)
    return n % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

// Excel PERCENTILE.INC / R type-7 method — the most widely expected
// convention since it matches Excel's default PERCENTILE function
function percentileValue(arr: number[], p: number): number {
    if (arr.length === 0) return 0
    const sorted = [...arr].sort((a, b) => a - b)
    const n = sorted.length
    const rank = (p / 100) * (n - 1)
    const lowerIdx = Math.floor(rank)
    const upperIdx = Math.ceil(rank)
    const frac = rank - lowerIdx
    if (upperIdx >= n) return sorted[n - 1]
    return sorted[lowerIdx] + frac * (sorted[upperIdx] - sorted[lowerIdx])
}

function quartilesOf(arr: number[]): { q1: number; q2: number; q3: number } {
    return { q1: percentileValue(arr, 25), q2: percentileValue(arr, 50), q3: percentileValue(arr, 75) }
}

function varianceOf(arr: number[], isSample: boolean): number {
    if (arr.length === 0) return 0
    const m = mean(arr)
    const sumSq = arr.reduce((acc, v) => acc + (v - m) ** 2, 0)
    const denom = isSample ? Math.max(arr.length - 1, 1) : arr.length
    return sumSq / denom
}

// "Mean, Median, Mode & Range Calculator"
const NO_MODE: Record<string, string> = {
    en: 'No mode (all values unique)', ru: 'Моды нет (все значения уникальны)', de: 'Kein Modus (alle Werte eindeutig)', es: 'Sin moda (todos los valores son únicos)', fr: 'Pas de mode (toutes les valeurs sont uniques)', it: 'Nessuna moda (tutti i valori sono unici)', pl: 'Brak mody (wszystkie wartości są unikalne)', lv: 'Nav modas (visas vērtības unikālas)',
}
export function meanMedianModeRangeCalculator(params: { data: unknown; language: unknown }): {
    mean: number; median: number; mode: string; range: number
} {
    const arr = parseData(params.data)
    if (arr.length === 0) return { mean: 0, median: 0, mode: t(NO_MODE, params.language), range: 0 }
    const counts = new Map<number, number>()
    for (const v of arr) counts.set(v, (counts.get(v) || 0) + 1)
    const maxCount = Math.max(...counts.values())
    const modes = maxCount === 1 ? [] : [...counts.entries()].filter(([, c]) => c === maxCount).map(([v]) => v).sort((a, b) => a - b)
    return {
        mean: mean(arr),
        median: medianOf(arr),
        mode: modes.length > 0 ? modes.join(', ') : t(NO_MODE, params.language),
        range: Math.max(...arr) - Math.min(...arr),
    }
}

// "Standard Deviation Calculator"
export function standardDeviationCalculator(params: { data: unknown; type: unknown }): {
    standard_deviation: number; mean: number; variance: number
} {
    const arr = parseData(params.data)
    const isSample = String(params.type) !== 'population'
    const varianceVal = varianceOf(arr, isSample)
    return { standard_deviation: Math.sqrt(varianceVal), mean: mean(arr), variance: varianceVal }
}

// "Variance Calculator"
export function varianceCalculator(params: { data: unknown; type: unknown }): {
    variance: number; mean: number; standard_deviation: number
} {
    const arr = parseData(params.data)
    const isSample = String(params.type) !== 'population'
    const varianceVal = varianceOf(arr, isSample)
    return { variance: varianceVal, mean: mean(arr), standard_deviation: Math.sqrt(varianceVal) }
}

// "Z-Score Calculator"
const ABOVE_MEAN: Record<string, string> = {
    en: 'Above the mean', ru: 'Выше среднего', de: 'Über dem Mittelwert', es: 'Por encima de la media', fr: 'Au-dessus de la moyenne', it: 'Sopra la media', pl: 'Powyżej średniej', lv: 'Virs vidējā',
}
const BELOW_MEAN: Record<string, string> = {
    en: 'Below the mean', ru: 'Ниже среднего', de: 'Unter dem Mittelwert', es: 'Por debajo de la media', fr: 'En dessous de la moyenne', it: 'Sotto la media', pl: 'Poniżej średniej', lv: 'Zem vidējā',
}
const EQUAL_TO_MEAN: Record<string, string> = {
    en: 'Exactly the mean', ru: 'Точно среднее', de: 'Genau der Mittelwert', es: 'Exactamente la media', fr: 'Exactement la moyenne', it: 'Esattamente la media', pl: 'Dokładnie średnia', lv: 'Precīzi vidējais',
}
export function zScoreCalculator(params: { value: unknown; mean: unknown; standard_deviation: unknown; language: unknown }): {
    z_score: number; interpretation: string
} {
    const value = Number(params.value) || 0
    const meanVal = Number(params.mean) || 0
    const sd = Number(params.standard_deviation) || 1
    const z = (value - meanVal) / sd
    const interpretation = z > 0 ? ABOVE_MEAN : z < 0 ? BELOW_MEAN : EQUAL_TO_MEAN
    return { z_score: z, interpretation: t(interpretation, params.language) }
}

// "Percentile Calculator" - finds the value at a given percentile using
// the Excel PERCENTILE.INC / R type-7 interpolation method
export function percentileCalculator(params: { data: unknown; percentile: unknown }): { value_at_percentile: number } {
    const arr = parseData(params.data)
    const p = Math.min(Math.max(Number(params.percentile) || 0, 0), 100)
    return { value_at_percentile: percentileValue(arr, p) }
}

// "Quartile & IQR Calculator"
export function quartileIqrCalculator(params: { data: unknown }): { q1: number; q2: number; q3: number; iqr: number } {
    const arr = parseData(params.data)
    const q = quartilesOf(arr)
    return { q1: q.q1, q2: q.q2, q3: q.q3, iqr: q.q3 - q.q1 }
}

// "Five Number Summary Calculator"
export function fiveNumberSummaryCalculator(params: { data: unknown }): {
    minimum: number; q1: number; median: number; q3: number; maximum: number
} {
    const arr = parseData(params.data)
    if (arr.length === 0) return { minimum: 0, q1: 0, median: 0, q3: 0, maximum: 0 }
    const q = quartilesOf(arr)
    return { minimum: Math.min(...arr), q1: q.q1, median: q.q2, q3: q.q3, maximum: Math.max(...arr) }
}

// "Outlier Calculator (IQR Method)" - flags values outside 1.5x IQR
// fences, the standard convention (also used for box plot whiskers)
const NO_OUTLIERS: Record<string, string> = {
    en: 'None detected', ru: 'Не обнаружено', de: 'Keine erkannt', es: 'Ninguno detectado', fr: 'Aucun détecté', it: 'Nessuno rilevato', pl: 'Nie wykryto', lv: 'Nav atrasts',
}
export function outlierCalculator(params: { data: unknown; language: unknown }): {
    lower_fence: number; upper_fence: number; outliers: string
} {
    const arr = parseData(params.data)
    const q = quartilesOf(arr)
    const iqr = q.q3 - q.q1
    const lowerFence = q.q1 - 1.5 * iqr
    const upperFence = q.q3 + 1.5 * iqr
    const outliers = arr.filter((v) => v < lowerFence || v > upperFence)
    return {
        lower_fence: lowerFence,
        upper_fence: upperFence,
        outliers: outliers.length > 0 ? outliers.join(', ') : t(NO_OUTLIERS, params.language),
    }
}

// "Coefficient of Variation Calculator" - always uses sample standard
// deviation, the more common default for a quick dataset summary
const CV_LOW: Record<string, string> = {
    en: 'Low variability', ru: 'Низкая изменчивость', de: 'Geringe Variabilität', es: 'Baja variabilidad', fr: 'Faible variabilité', it: 'Bassa variabilità', pl: 'Niska zmienność', lv: 'Zema mainība',
}
const CV_MODERATE: Record<string, string> = {
    en: 'Moderate variability', ru: 'Умеренная изменчивость', de: 'Mäßige Variabilität', es: 'Variabilidad moderada', fr: 'Variabilité modérée', it: 'Variabilità moderata', pl: 'Umiarkowana zmienność', lv: 'Vidēja mainība',
}
const CV_HIGH: Record<string, string> = {
    en: 'High variability', ru: 'Высокая изменчивость', de: 'Hohe Variabilität', es: 'Alta variabilidad', fr: 'Variabilité élevée', it: 'Alta variabilità', pl: 'Wysoka zmienność', lv: 'Augsta mainība',
}
export function coefficientOfVariationCalculator(params: { data: unknown; language: unknown }): {
    coefficient_of_variation: number; interpretation: string
} {
    const arr = parseData(params.data)
    const m = mean(arr)
    const sd = Math.sqrt(varianceOf(arr, true))
    const cv = m !== 0 ? (sd / Math.abs(m)) * 100 : 0
    let bucket: Record<string, string>
    if (cv < 15) bucket = CV_LOW
    else if (cv < 35) bucket = CV_MODERATE
    else bucket = CV_HIGH
    return { coefficient_of_variation: cv, interpretation: t(bucket, params.language) }
}

// "Weighted Average Calculator" - pairs values[i] with weights[i]
export function weightedAverageCalculator(params: { values_list: unknown; weights_list: unknown }): { weighted_average: number } {
    const values = parseData(params.values_list)
    const weights = parseData(params.weights_list)
    const n = Math.min(values.length, weights.length)
    let sumProduct = 0
    let sumWeights = 0
    for (let i = 0; i < n; i++) {
        sumProduct += values[i] * weights[i]
        sumWeights += weights[i]
    }
    return { weighted_average: sumWeights !== 0 ? sumProduct / sumWeights : 0 }
}

// "Geometric Mean Calculator" - requires strictly positive values (the
// mathematical definition breaks down for zero/negative numbers), so
// non-positive entries are silently excluded rather than producing NaN
export function geometricMeanCalculator(params: { data: unknown }): { geometric_mean: number } {
    const arr = parseData(params.data).filter((v) => v > 0)
    if (arr.length === 0) return { geometric_mean: 0 }
    const logSum = arr.reduce((acc, v) => acc + Math.log(v), 0)
    return { geometric_mean: Math.exp(logSum / arr.length) }
}

// "Frequency Distribution Table Calculator" - counts occurrences of each
// unique value in the dataset (not a binned/grouped histogram)
export function frequencyDistributionCalculator(params: { data: unknown }): { frequency_table: string } {
    const arr = parseData(params.data)
    if (arr.length === 0) return { frequency_table: '' }
    const counts = new Map<number, number>()
    for (const v of arr) counts.set(v, (counts.get(v) || 0) + 1)
    const sorted = [...counts.entries()].sort((a, b) => a[0] - b[0])
    const total = arr.length
    return { frequency_table: sorted.map(([v, c]) => `${v}: ${c} (${((c / total) * 100).toFixed(1)}%)`).join('\n') }
}
